import type { Renderer, RendererHooks } from '../renderer';
import { createProjection, composeCamera, SAFE_INSET_CSS, type Projection } from '../projection';
import type { AppState } from '../../state/default-state';
import { effectiveWorldPosition } from '../../state/phase';
import { getBody } from '../../core/reference-data';
import { maxApoapsis } from '../../core/orbit-geometry';
import type { Body, BodyId, BodyStatus } from '../../core/types';

/**
 * Enhanced WebGL adapter (Story 1.6, AD-3 / AD-9 / AD-10). This is a DECORATION
 * overlay, not a replacement renderer: the Canvas2D baseline (CanvasHost) always
 * owns the geometry surface and draws every orbit line, body marker, selection
 * ring, centre label, and the model notice, so simulation correctness is never at
 * risk. This overlay adds only degradable decoration (additive glow/particles at
 * body positions) on its own transparent canvas. Keeping the complete baseline on
 * a separate 2D canvas is the literal meaning of AD-3 ("Canvas2D is the COMPLETE
 * baseline; the WebGL adapter adds decoration only") and keeps `getContext('2d')`
 * always valid on the baseline surface.
 *
 * It reads positions from `core` and consumes the ONE shared projection
 * (AD-10) identically to the Canvas2D adapter, recomputing no orbital math (AD-1).
 * Under effective reduced-motion / reduced-effects it draws nothing: decoration
 * degrades before correctness (AD-9). A `webglcontextlost` is signalled OUT via
 * the injected `onContextLost` hook (render never dispatches, AD-2); the ui/app
 * layer turns that into a safe Compatibility fallback with no loss of state.
 */

type GL = WebGLRenderingContext | WebGL2RenderingContext;

const VERTEX_SRC = `
attribute vec2 a_clip;
attribute float a_size;
attribute vec3 a_colour;
attribute float a_alpha;
varying vec3 v_colour;
varying float v_alpha;
void main() {
  gl_Position = vec4(a_clip, 0.0, 1.0);
  gl_PointSize = a_size;
  v_colour = a_colour;
  v_alpha = a_alpha;
}
`;

// Soft radial glow falloff from the point centre; additive-blended for a halo.
const FRAGMENT_SRC = `
precision mediump float;
varying vec3 v_colour;
varying float v_alpha;
void main() {
  vec2 d = gl_PointCoord - vec2(0.5);
  float r = length(d) * 2.0;
  float alpha = smoothstep(1.0, 0.0, r) * v_alpha;
  gl_FragColor = vec4(v_colour * alpha, alpha);
}
`;

// Decorative glow sprite sizes in CSS pixels (multiplied by dpr at draw time).
// These are pure decoration (AD-9); the Canvas2D baseline draws the real markers.
// The central halo is deliberately kept close to the planet glow: at the default
// fit-all camera the four inner planets sit within ~20px of centre, so an oversized
// Sun halo swallows them. A modest halo keeps the Sun emphasised without eating the
// inner system, and orbit distances stay honest (linear, never rescaled).
const CENTRAL_GLOW_CSS = 44;
const PLANET_GLOW_CSS = 26;

export interface GlowBloomLayer {
  readonly sizeCss: number;
  readonly alpha: number;
}

type GlowBloomKind = 'central' | 'planet';

const CENTRAL_BLOOM_SCALE_ALPHA: readonly [number, number][] = [
  [1.45, 0.2],
  [0.95, 0.42],
  [0.42, 0.72]
];

const PLANET_BLOOM_SCALE_ALPHA: readonly [number, number][] = [
  [1.35, 0.16],
  [0.86, 0.34],
  [0.38, 0.66]
];

export function glowBloomLayers(
  baseSizeCss: number,
  kind: GlowBloomKind
): readonly GlowBloomLayer[] {
  const layers = kind === 'central' ? CENTRAL_BLOOM_SCALE_ALPHA : PLANET_BLOOM_SCALE_ALPHA;
  return layers.map(([scale, alpha]) => ({
    sizeCss: baseSizeCss * scale,
    alpha
  }));
}

function compile(gl: GL, type: number, src: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('WebGL shader allocation failed.');
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? 'unknown';
    gl.deleteShader(shader);
    throw new Error(`WebGL shader compile failed: ${log}`);
  }
  return shader;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16) / 255,
    parseInt(h.slice(2, 4), 16) / 255,
    parseInt(h.slice(4, 6), 16) / 255
  ];
}

export function createWebglRenderer(
  canvas: HTMLCanvasElement,
  hooks?: RendererHooks
): Renderer {
  const rawGl = (canvas.getContext('webgl2', { alpha: true, premultipliedAlpha: false }) ??
    canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false }) ??
    canvas.getContext('experimental-webgl', {
      alpha: true,
      premultipliedAlpha: false
    })) as GL | null;
  if (!rawGl) {
    // Construction failure is surfaced to the composition root (CanvasHost), which
    // falls back to the Canvas2D baseline with no loss of state (AD-3).
    throw new Error('WebGL context unavailable.');
  }
  const gl: GL = rawGl;

  const osReducedMotion = hooks?.reducedMotion === true;

  // Context loss is a recoverable event signalled OUT to the outer layer (AD-2):
  // render never dispatches. preventDefault keeps the browser from making the loss
  // permanent before the fallback swap runs.
  const onLost = (event: Event): void => {
    event.preventDefault();
    hooks?.onContextLost?.();
  };
  canvas.addEventListener('webglcontextlost', onLost, false);

  const vs = compile(gl, gl.VERTEX_SHADER, VERTEX_SRC);
  const fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SRC);
  const program = gl.createProgram();
  if (!program) throw new Error('WebGL program allocation failed.');
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program) ?? 'unknown';
    throw new Error(`WebGL program link failed: ${log}`);
  }
  const buffer = gl.createBuffer();
  const locClip = gl.getAttribLocation(program, 'a_clip');
  const locSize = gl.getAttribLocation(program, 'a_size');
  const locColour = gl.getAttribLocation(program, 'a_colour');
  const locAlpha = gl.getAttribLocation(program, 'a_alpha');

  let cssWidth = canvas.clientWidth || 1366;
  let cssHeight = canvas.clientHeight || 768;
  let dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  // The projection last rendered with, exposed via getProjection (AD-10). This
  // decoration overlay shares the identical transform as the Canvas2D baseline.
  let lastProjection: Projection | null = null;

  // --- Illustrative collision burst (Story 4.1, Task 8; Enhanced decoration only) --
  // Mirrors the Canvas2D baseline burst but as additive glow particles. Strictly
  // INERT when no active -> removed transition occurred and no burst is live, so a
  // no-collision frame is unchanged. Decoration (AD-9): it never gates correctness or
  // feeds the camera. Under the reduced signal this overlay draws nothing at all (the
  // early return below), and the baseline's static ring carries the indication (AC 7).
  // Error family (DESIGN.md, reserved for absorption) as normalised RGB, so the
  // central-absorption implosion reads distinctly from a collision's identity glow.
  const ERROR_RGB = hexToRgb('#ff5449');
  const prevStatus = new Map<BodyId, BodyStatus>();
  const lastScreen = new Map<BodyId, { x: number; y: number }>();
  interface Burst {
    x: number;
    y: number;
    colour: [number, number, number];
    /** 'collided' (outward) vs 'absorbed' (inward implosion). */
    kind: BodyStatus;
    start: number;
  }
  let bursts: Burst[] = [];
  const BURST_DURATION_MS = 900;
  const nowMs = (): number =>
    typeof performance !== 'undefined' ? performance.now() : Date.now();

  function applyBackingSize(): void {
    canvas.width = Math.max(1, Math.round(cssWidth * dpr));
    canvas.height = Math.max(1, Math.round(cssHeight * dpr));
  }
  applyBackingSize();

  function render(state: AppState): void {
    // Status diff FIRST (before any early return) so prevStatus stays fresh and no
    // stale transition fires when toggling the reduced signal. A burst spawns only
    // when the removed body has a known last screen position (i.e. it was drawn while
    // Enhanced was active and unreduced); otherwise the baseline owns the indication.
    for (const b of state.bodies) {
      const prev = prevStatus.get(b.id);
      if (prev === 'active' && b.status !== 'active') {
        const at = lastScreen.get(b.id);
        const colour = b.status === 'absorbed' ? ERROR_RGB : hexToRgb(getBody(b.id).colour);
        if (at) bursts.push({ x: at.x, y: at.y, colour, kind: b.status, start: nowMs() });
      }
      prevStatus.set(b.id, b.status);
    }

    // Prune expired bursts BEFORE any early return (Finding 2). The reduced-motion and
    // realistic-scale early returns below skip the draw block's prune, so without this
    // the array would grow unboundedly in those modes as active->removed transitions
    // keep pushing entries. Bursts are bounded to the last BURST_DURATION_MS regardless
    // of mode; normal-mode decoration is unchanged (the draw block re-prunes harmlessly).
    if (bursts.length > 0) {
      const t = nowMs();
      bursts = bursts.filter((b) => t - b.start < BURST_DURATION_MS);
    }

    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // Degrade decoration first (AD-9): under EITHER the OS reduced-motion signal
    // or the in-app reduced-effects toggle (the union, Consistency Conventions),
    // the overlay draws nothing. The baseline still draws every state cue, so no
    // information is hidden.
    const reduced = osReducedMotion || state.reducedEffects;
    if (reduced) return;

    // Realistic scale mode (Story 3.3, AC6 / NFR24): suppress the fixed-size glow
    // halos. A fixed-size halo would imply a body far larger than
    // its true tiny radius, misrepresenting physical size. The overlay is decoration
    // only (AD-9), so drawing nothing here is safe: the Canvas2D baseline still draws
    // every locator, label, orbit line, and selection ring.
    if (state.scaleMode === 'realistic') return;

    // Same geometry source and projection as the Canvas2D baseline (AD-10): the
    // glow sits exactly on each body. No orbital math is recomputed here (AD-1).
    // The `status === 'active'` filter is IDENTICAL to the Canvas2D baseline so
    // both adapters feed the shared projection the same camera-fit inputs (Finding
    // 2); Epic 4 will decide the camera semantics for removed bodies.
    const orbiting: Body[] = state.bodies
      .filter((b) => b.id !== state.centralBody && b.status === 'active')
      .map((b) => ({ ...getBody(b.id), a: b.a, e: b.e }));
    const central = getBody(state.centralBody);

    const viewport = { cssWidth, cssHeight, dpr };
    // Consume the SAME composed camera as the Canvas2D baseline (AD-10 / AC-6): the
    // glow overlay must frame identically under any zoom/pan so the decoration
    // stays aligned with the geometry surface.
    const camera = composeCamera(
      viewport,
      maxApoapsis(orbiting),
      state.camera.zoom,
      state.camera.panX,
      state.camera.panY,
      SAFE_INSET_CSS
    );
    const projection = createProjection(viewport, camera);
    lastProjection = projection;

    // Build interleaved point data: [clipX, clipY, size, r, g, b, alpha] per glow.
    const verts: number[] = [];
    const pushSprite = (
      px: number,
      py: number,
      sizePx: number,
      colour: [number, number, number],
      alpha: number
    ): void => {
      const clipX = (px / canvas.width) * 2 - 1;
      const clipY = 1 - (py / canvas.height) * 2;
      const [r, g, b] = colour;
      verts.push(clipX, clipY, sizePx, r, g, b, alpha);
    };
    const pushBloom = (
      px: number,
      py: number,
      baseSizeCss: number,
      kind: GlowBloomKind,
      colour: string
    ): void => {
      const [r, g, b] = hexToRgb(colour);
      for (const layer of glowBloomLayers(baseSizeCss, kind)) {
        pushSprite(px, py, layer.sizeCss * dpr, [r, g, b], layer.alpha);
      }
    };

    // The SUN always gets the strongest bloom, wherever it is — as the centre OR as
    // a swapped-out orbiter — because it is the light source; every other body gets
    // the softer planet bloom (so a non-Sun central body does not glow like a star).
    // This is Enhanced-only decoration: the Canvas2D baseline still owns the body
    // markers, labels, orbit lines, sectors, and state cues.
    const bloomKindFor = (id: string): GlowBloomKind => (id === 'sun' ? 'central' : 'planet');
    const bloomSizeFor = (id: string): number => (id === 'sun' ? CENTRAL_GLOW_CSS : PLANET_GLOW_CSS);
    const centre = projection.worldToScreen(0, 0);
    pushBloom(
      centre.x,
      centre.y,
      bloomSizeFor(state.centralBody),
      bloomKindFor(state.centralBody),
      central.colour
    );
    for (const body of orbiting) {
      // Effective phase (Story 1.2 motion + Story 1.7 dragged offset), shared with
      // the baseline so the glow stays exactly on each body (AD-10).
      const offset = state.phaseOffsets[body.id] ?? 0;
      const pos = effectiveWorldPosition(body, central.mu, state.time, offset);
      const screen = projection.worldToScreen(pos.x, pos.y);
      lastScreen.set(body.id, { x: screen.x, y: screen.y });
      pushBloom(screen.x, screen.y, bloomSizeFor(body.id), bloomKindFor(body.id), body.colour);
    }

    // Collision burst particles (decoration only). Growing, fading additive glow at
    // each removed body's last position. Inert unless a burst is live.
    if (bursts.length > 0) {
      const t = nowMs();
      bursts = bursts.filter((b) => t - b.start < BURST_DURATION_MS);
      for (const b of bursts) {
        const p = (t - b.start) / BURST_DURATION_MS;
        const fade = Math.max(0, 1 - p);
        // Collision expands (20 -> 110); absorption contracts inward (110 -> 20).
        const size = (b.kind === 'absorbed' ? 110 - p * 90 : 20 + p * 90) * dpr;
        // Fade by dimming the colour (the fragment multiplies colour by coverage).
        pushSprite(
          b.x,
          b.y,
          size,
          [b.colour[0] * fade, b.colour[1] * fade, b.colour[2] * fade],
          1
        );
      }
    }

    if (verts.length === 0) return;

    gl.useProgram(program);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE); // additive glow
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(verts), gl.DYNAMIC_DRAW);
    const stride = 7 * 4;
    gl.enableVertexAttribArray(locClip);
    gl.vertexAttribPointer(locClip, 2, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(locSize);
    gl.vertexAttribPointer(locSize, 1, gl.FLOAT, false, stride, 2 * 4);
    gl.enableVertexAttribArray(locColour);
    gl.vertexAttribPointer(locColour, 3, gl.FLOAT, false, stride, 3 * 4);
    gl.enableVertexAttribArray(locAlpha);
    gl.vertexAttribPointer(locAlpha, 1, gl.FLOAT, false, stride, 6 * 4);
    gl.drawArrays(gl.POINTS, 0, verts.length / 7);
  }

  return {
    render,
    getProjection(): Projection | null {
      return lastProjection;
    },
    resize(nextCssWidth: number, nextCssHeight: number, nextDpr: number) {
      cssWidth = nextCssWidth > 0 ? nextCssWidth : cssWidth;
      cssHeight = nextCssHeight > 0 ? nextCssHeight : cssHeight;
      dpr = nextDpr > 0 ? nextDpr : dpr;
      applyBackingSize();
    },
    destroy() {
      canvas.removeEventListener('webglcontextlost', onLost, false);
      try {
        // Clear any last decoration frame so switching to Compatibility leaves no
        // stale glow on the (transparent) overlay canvas.
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.deleteBuffer(buffer);
        gl.deleteProgram(program);
        gl.deleteShader(vs);
        gl.deleteShader(fs);
      } catch {
        // A destroyed/lost context may throw on cleanup; the overlay is decoration
        // only, so a failed teardown never affects the baseline or state.
      }
    }
  };
}
